import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import Header from '../components/Header';
import Button from '../components/Button';
import { bookCounselingSlot, getCounselingByTest, getMeetingLink } from '../services/api';
import { colors } from '../theme';
import { toIST } from '../utils/dateUtils';

const BookCounselingContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #333;
`;

const Subtitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #555;
`;

const Message = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  color: #666;
`;

const DateSelector = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;
  overflow-x: auto;
  padding-bottom: 1rem;
`;

const DateCard = styled.div`
  border: 1px solid ${props => props.selected ? '#4caf50' : '#ddd'};
  border-radius: 8px;
  padding: 1rem;
  min-width: 100px;
  text-align: center;
  cursor: pointer;
  background-color: ${props => props.selected ? '#e8f5e9' : 'white'};
  
  &:hover {
    border-color: #4caf50;
  }
`;

const DateDay = styled.div`
  font-weight: 600;
  color: ${props => props.selected ? '#4caf50' : '#333'};
`;

const DateDate = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: ${props => props.selected ? '#4caf50' : '#333'};
`;

const DateSlots = styled.div`
  font-size: 0.8rem;
  color: ${props => props.selected ? '#4caf50' : '#666'};
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TimeSlot = styled.div`
  padding: 0.75rem;
  border: 1px solid ${props => props.selected ? '#4caf50' : props.disabled ? '#ddd' : '#ddd'};
  border-radius: 4px;
  text-align: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background-color: ${props => props.selected ? '#e8f5e9' : 'white'};
  color: ${props => props.selected ? '#4caf50' : props.disabled ? '#666' : '#333'};
  
  &:hover {
    border-color: ${props => props.disabled ? '#ddd' : '#4caf50'};
  }
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ActionButton = styled(Button)`
  margin-top: 1.5rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
  color: #666;
`;

const SuccessCard = styled(Card)`
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
`;

const StepContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 1.5rem;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.active ? colors.softPurple : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border: 1px solid ${props => props.active ? colors.softPurple : '#ddd'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StepLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.active ? colors.softPurple : '#666'};
`;

const BookCounseling = () => {
  const { testBookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counselingBooked, setCounselingBooked] = useState(false);
  const [existingBooking, setExistingBooking] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);
  
  // Use a ref to track if the API call has been made
  const apiCallMade = useRef(false);
  
  // Get Fuser from local storage
  const user = JSON.parse(localStorage.getItem('user'));
  
  // Check if we're rescheduling
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const reschedule = queryParams.get('reschedule');
    setIsRescheduling(reschedule === 'true');
  }, [location]);
  
  // Generate available dates for counseling (today and next 4 days)
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    
    // Start from today
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      
      const day = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
      const dateStr = `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;
      
      dates.push({
        day: day,
        date: dateStr,
        slots: Math.floor(Math.random() * 10) + 5 // Random number of slots between 5-15
      });
      
      // Stop after we have 7 dates
      if (dates.length >= 4) {
        break;
      }
    }
    
    console.log('Generated dates:', dates);
    return dates;
  };
  
  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };
  
  const handleSelectTime = (time) => {
    setSelectedTime(time);
  };

  // Check if a time slot should be disabled based on selected date
  const isTimeSlotDisabled = (hour, minute, selectedDateStr) => {
    const today = new Date();
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    const currentDateStr = `${today.getDate()} ${today.toLocaleDateString('en-US', { month: 'short' })}`;
    
    // If selected date is today, disable past time slots
    if (selectedDateStr === currentDateStr) {
      return (hour < currentHour || (hour === currentHour && minute <= currentMinute));
    }
    
    // For future dates, all time slots are enabled
    return false;
  };
  
  // Generate available time slots (11 AM to 8 PM, 30-min intervals)
  const generateTimeSlots = (selectedDateStr) => {
    const slots = [];
    
    // Start from 11 AM
    const startHour = 11;
    const endHour = 21; // 8:30 PM
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour > 12 ? hour - 12 : hour;
        const amPm = hour >= 12 ? 'PM' : 'AM';
        const formattedMinute = minute === 0 ? '00' : minute;
        
        slots.push({
          time: `${formattedHour}:${formattedMinute} ${amPm}`,
          disabled: isTimeSlotDisabled(hour, minute, selectedDateStr)
        });
      }
    }
    
    return slots;
  };
  
  const dates = generateDates();
  const [timeSlots, setTimeSlots] = useState([]);
  
  // Update time slots when selected date changes
  useEffect(() => {
    if (selectedDate) {
      setTimeSlots(generateTimeSlots(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    const checkExistingBooking = async () => {
      // If we've already made the API call, don't make it again
      if (apiCallMade.current) return;
      
      try {
        setLoading(true);
        
        if (!user || !testBookingId) {
          setError('Missing user or test booking information');
          return;
        }
        
        // Check if counseling is already booked for this test
        const counselingData = await getCounselingByTest(testBookingId);
        console.log('Counseling data:', counselingData);
        
        if (counselingData) {
          setExistingBooking(counselingData);
          
          // If we're not rescheduling, set counselingBooked to true
          if (!isRescheduling) {
            setCounselingBooked(true);
          }
          
          // Set the selected date and time from the booked counseling
          const counselingDate = toIST(new Date(counselingData.timeslot));
          const dateStr = `${counselingDate.getDate()} ${counselingDate.toLocaleDateString('en-US', { month: 'short' })}`;
          const timeStr = counselingDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          
          setSelectedDate(dateStr);
          setSelectedTime(timeStr);
        }
      } catch (error) {
        console.error('Error checking existing booking:', error);
        // If no booking exists, that's fine, we'll show the booking form
      } finally {
        setLoading(false);
        // Mark that we've made the API call
        apiCallMade.current = true;
      }
    };
    
    // Only run once when the component mounts
    checkExistingBooking();
  }, [testBookingId, user, isRescheduling]); // Keep dependencies but use ref to prevent multiple calls
  
  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }
    
    try {
      setLoading(true);
      
      // Convert selected date and time to a datetime object
      const [day, month] = selectedDate.split(' ');
      const year = new Date().getFullYear();
      const dateTimeString = `${month} ${day} ${year} ${selectedTime}`;
      const selectedDateTime = new Date(dateTimeString);
      
      console.log('Selected date and time:', selectedDateTime);
      
      // Prepare booking data
      const bookingData = {
        user_id: parseInt(user.user_id),
        timeslot: selectedDateTime.toISOString(),
        test_booking_id: parseInt(testBookingId)
      };
      
      console.log('Booking data to send:', bookingData);
      
      // Call the API to book the counseling session
      const response = await bookCounselingSlot(bookingData);
      console.log('Booking response:', response);
      
      if (response && response.booking_id) {
        toast.success(isRescheduling ? 'Counseling session rescheduled successfully!' : 'Counseling session booked successfully!');
        // Redirect to the counseling success page
        navigate(`/book-counseling/${testBookingId}`);
        window.location.reload();
      } else {
        toast.error(isRescheduling ? 'Failed to reschedule counseling session. Please try again.' : 'Failed to book counseling session. Please try again.');
      }
    } catch (error) {
      console.error('Error booking counseling slot:', error);
      toast.error(error.response?.data?.detail || 'Failed to book counseling slot. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleJoinMeeting = async () => {
    try {
      // Fetch the meeting link directly from the meeting links table
      const response = await getMeetingLink();
      
      if (response && response.meeting_url) {
        window.open(response.meeting_url, '_blank');
      } else {
        toast.error('Meeting link not available');
      }
    } catch (error) {
      console.error('Error fetching meeting link:', error);
      toast.error('Failed to get meeting link. Please try again.');
    }
  };
  
  const formatDateTime = (dateTimeString) => {
    // Convert UTC time to IST
    const date = toIST(new Date(dateTimeString));
    return format(date, 'EEEE, MMMM d, yyyy h:mm a') + ' IST';
  };

  const handleStepClick = (step) => {
    if (step === 1) {
      navigate(`/test-success/${testBookingId}`);
    }
  };
  
  if (loading) {
    return (
      <BookCounselingContainer>
        <Header />
        <Content>
          <LoadingMessage>Loading...</LoadingMessage>
        </Content>
      </BookCounselingContainer>
    );
  }
  
  if (error) {
    return (
      <BookCounselingContainer>
        <Header />
        <Content>
          <Card>
            <Title>Error</Title>
            <Message>{error}</Message>
            <ActionButton onClick={() => navigate('/')}>
              Back to Dashboard
            </ActionButton>
          </Card>
        </Content>
      </BookCounselingContainer>
    );
  }
  
  if (counselingBooked && existingBooking) {
    return (
      <BookCounselingContainer>
        <Header />
        <Content>
          <StepContainer>
            <Step 
              clickable={true} 
              onClick={() => handleStepClick(1)}
            >
              <StepNumber active={false}>1</StepNumber>
              <StepLabel active={false}>Qualifier Test</StepLabel>
            </Step>
            <Step>
              <StepNumber active={true}>2</StepNumber>
              <StepLabel active={true}>Secure Seat</StepLabel>
            </Step>
            <Step>
              <StepNumber active={false}>3</StepNumber>
              <StepLabel active={false}>Complete Admission</StepLabel>
            </Step>
            <Step>
              <StepNumber active={false}>4</StepNumber>
              <StepLabel active={false}>Join Program</StepLabel>
            </Step>
          </StepContainer>

          <SuccessCard>
            <SuccessIcon>✓</SuccessIcon>
            <Title>Counseling Session Booked!</Title>
            <Message>
              Your counseling session is scheduled for <strong>{formatDateTime(existingBooking.timeslot)}</strong>
            </Message>
            
            
            {/* {existingBooking.can_join ? ( */}
            {/* <ButtonGroup> */}
              <ActionButton 
                size="large" 
                onClick={handleJoinMeeting}
                fullWidth
              >
                Join Meeting Now
              </ActionButton>

              <div style={{ marginTop: '1rem' }}>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  style={{
                    backgroundColor: 'transparent',
                    color: 'black',
                  }}
                >
                  Back to Dashboard
                </Button>
              </div>
              
              
            {/* ) : (
              <>
                <Message>
                  You will be able to join the meeting 5 minutes before the scheduled time.
                </Message>
                <ActionButton 
                  size="large" 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  fullWidth
                >
                  Back to Dashboard
                </ActionButton>
              </>
            )}
            */}
            
            {/* <ActionButton 
              size="large" 
              onClick={() => setIsRescheduling(true)}
              variant="secondary"
              fullWidth
            >
              Reschedule Session
            </ActionButton> */}
            {/* </ButtonGroup> */}
          </SuccessCard>

        </Content>
      </BookCounselingContainer>
    );
  }
  
  return (
    <BookCounselingContainer>
      <Header />
      <Content>
        <StepContainer>
          <Step 
            clickable={true} 
            onClick={() => handleStepClick(1)}
          >
            <StepNumber active={false}>1</StepNumber>
            <StepLabel active={false}>Qualifier Test</StepLabel>
          </Step>
          <Step>
            <StepNumber active={true}>2</StepNumber>
            <StepLabel active={true}>Secure Seat</StepLabel>
          </Step>
          <Step>
            <StepNumber active={false}>3</StepNumber>
            <StepLabel active={false}>Complete Admission</StepLabel>
          </Step>
          <Step>
            <StepNumber active={false}>4</StepNumber>
            <StepLabel active={false}>Join Program</StepLabel>
          </Step>
        </StepContainer>

        <Card>
          <Title>{isRescheduling ? 'Reschedule Counseling Session' : 'Book Counseling Session'}</Title>
          <Message>
            {isRescheduling 
              ? 'Select a new time slot for your counseling session.' 
              : 'Select a time slot for your counseling session. The counselor will discuss your test results and guide you on the next steps.'}
          </Message>
          
          <div>
            <Subtitle>Select date</Subtitle>
            <DateSelector>
              {dates.map((date, index) => (
                <DateCard 
                  key={index} 
                  selected={selectedDate === date.date}
                  onClick={() => handleSelectDate(date.date)}
                >
                  <DateDay selected={selectedDate === date.date}>{date.day}</DateDay>
                  <DateDate selected={selectedDate === date.date}>{date.date}</DateDate>
                </DateCard>
              ))}
            </DateSelector>
          </div>
          
          {selectedDate && (
            <div>
              <Subtitle>Select time</Subtitle>
              <TimeSlotGrid>
                {timeSlots.map((timeSlot, index) => (
                  <TimeSlot 
                    key={index}
                    selected={selectedTime === timeSlot.time}
                    onClick={() => !timeSlot.disabled && handleSelectTime(timeSlot.time)}
                    disabled={timeSlot.disabled}
                  >
                    {timeSlot.time}
                  </TimeSlot>
                ))}
              </TimeSlotGrid>
            </div>
          )}
          
          <ActionButton 
            size="large" 
            onClick={handleBooking}
            disabled={!selectedTime || loading}
            fullWidth
          >
            {loading ? 'Processing...' : isRescheduling ? 'Reschedule Session' : 'Book Session'}
          </ActionButton>
          
          <ActionButton 
            size="large" 
            onClick={() => navigate('/')}
            variant="outline"
            fullWidth
            style={{ marginTop: '1rem' }}
          >
            Back to Dashboard
          </ActionButton>
        </Card>
      </Content>
    </BookCounselingContainer>
  );
};

export default BookCounseling;
